import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

/**
 * `disableHoverableContent` is the second half of making a tooltip a label
 * rather than an obstacle. `pointer-events-none` on the panel stops it
 * swallowing a click, but Radix separately keeps a tooltip *open* while the
 * pointer travels toward it — a grace area for tooltips you can mouse into. Ours
 * hold no interactive content, so that grace only buys a panel that lingers over
 * whatever sits beneath it after the cursor has moved on. Off, the tooltip
 * closes when its trigger is no longer hovered.
 */
const TooltipProvider = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider disableHoverableContent {...props}>
    {children}
  </TooltipPrimitive.Provider>
);

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          // `pointer-events-none`: a tooltip is a label, never a target. Without
          // it the panel slides under the cursor, holds itself open because the
          // pointer is now "inside" it, and swallows the click meant for what it
          // was describing. Nothing we put in one is interactive.
          "pointer-events-none z-9999 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// TODO: this is using the box shadow hack - see if we can use something like a rotate transform instead?
const TooltipArrow = TooltipPrimitive.Arrow;

export { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger };
